import type { ShallowRef } from 'vue'
import { useElementVisibility } from '@vueuse/core'

/**
 * Shared wiring for the mobile sticky buy-box bar (`EventStickyBuyBoxButton`),
 * used by both the event detail page (`EventDetails.vue`) and the kit/box detail
 * page (`BoxDetails.vue`).
 *
 * Given the page's `EventDetailsSidebar` template ref — which exposes
 * `buyBoxContainer` (the buy box / giftcard area) and `hasStickyBuyBoxTarget` —
 * it decides when the sticky bar shows and how to scroll to the buy box.
 *
 * The bar is hidden whenever the buy box area is in the viewport, and shown
 * whenever it is out of view (below the fold on load or scrolled past), as long
 * as the page actually renders a buy box / giftcard target.
 */
export const useStickyBuyBox = (sidebarRef: Readonly<ShallowRef<any>>) => {
  // Observe only the buy box / giftcard area exposed by the sidebar, ignoring the
  // team-event and corporate-gifting cards rendered below it.
  const buyBoxContainer = computed<HTMLElement | null>(
    () => sidebarRef.value?.buyBoxContainer ?? null
  )
  const hasStickyBuyBoxTarget = computed<boolean>(() =>
    Boolean(sidebarRef.value?.hasStickyBuyBoxTarget)
  )
  const isBuyBoxVisible = useElementVisibility(buyBoxContainer)

  // `visible` means "the buy box is on screen, so hide the sticky bar". Show the
  // bar only when there is a target and it is out of the viewport.
  const isSidebarVisible = computed<boolean>(
    () => !hasStickyBuyBoxTarget.value || isBuyBoxVisible.value
  )

  const scrollToSidebar = () => {
    const el = buyBoxContainer.value
    if (!el) return
    // subtract navbar height so the buy box isn't hidden behind it, plus a gap
    const top = el.getBoundingClientRect().top + window.scrollY - 115
    window.scrollTo({ top, behavior: 'smooth' })
  }

  return { buyBoxContainer, isSidebarVisible, scrollToSidebar }
}
